import { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  DollarSign,
  Users,
  Save,
  X
} from 'lucide-react';
import AdminHeader from '@/react-app/components/AdminHeader';
import type { SavingsPackage, PackageStats } from '@/shared/types';
import { api } from '@/shared/api/endpoints';
import { toast } from "react-toastify";
import { useAuthStore } from '@/shared/stores/authStore';




export default function PackageManagement() {
  const [packages, setPackages] = useState<SavingsPackage[]>([]);
  const [packageStats, setPackageStats] = useState<Record<string, PackageStats>>({});
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState<SavingsPackage | null>(null);
  const [working, setWorking] = useState(false);
  const { user: authStoreUser, isAuthenticated } = useAuthStore();

  if (!isAuthenticated || authStoreUser?.role == "user") {
    window.location.href = '/login';
    return null;
  }


  const [formData, setFormData] = useState({
    display_name: '',
    minimum_deposit: '',
    monthly_return_rate: '',
    referral_bonus_rate: '',
    description: '',
    is_active: true
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const [packagesResponse, statsResponse] = await Promise.all([
        api.package.getAll(),
        api.admin.getPackageStats()
      ]);


      if (packagesResponse.success && packagesResponse.data) {
        setPackages(packagesResponse.data || []);
      }

      if (statsResponse.success && statsResponse.data) {
        setPackageStats(statsResponse.data || {});
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setWorking(true);
    const payload = {
      ...formData,
      minimum_deposit: parseFloat(formData.minimum_deposit),
      monthly_return_rate: parseFloat(formData.monthly_return_rate),
      referral_bonus_rate: parseFloat(formData.referral_bonus_rate),
    };

    console.log(payload);

    try {


      const response = editingPackage
        ? await api.package.update(editingPackage._id, payload)
        : await api.package.create(payload);

      if (response.success) {
        fetchPackages();
        resetForm();
        toast.success("Package Updated");

      }
    } catch (error: any) {
      console.error('Error saving package:', error);
      toast.success("Package Update Failed", error);
    } finally {
      setWorking(false);
    }
  };

  const handleDelete = async (packageId: string) => {
    if (!confirm('Are you sure you want to delete this package? This action cannot be undone.')) {
      return;
    }

    try {

      const response = await api.package.remove(packageId);

      if (response.success) {
        fetchPackages();
      }
    } catch (error) {
      console.error('Error deleting package:', error);
    }
  };

  const handleToggleStatus = async (pkg: SavingsPackage) => {
    try {
      const payload = {
        is_active: pkg.status ? false : true
      }
      const response = await api.package.updateStatus(pkg._id, payload);
      if (response.success) {
        fetchPackages();
      }
    } catch (error) {
      console.error('Error toggling package status:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      display_name: '',
      minimum_deposit: '',
      monthly_return_rate: '',
      referral_bonus_rate: '',
      description: '',
      is_active: true
    });
    setEditingPackage(null);
    setShowAddModal(false);
  };

  const startEdit = (pkg: SavingsPackage) => {
    setFormData({
      display_name: pkg.name,
      minimum_deposit: pkg.minDeposit.toString(),
      monthly_return_rate: pkg.monthlyReturn.toString(),
      referral_bonus_rate: pkg.referralBonus.toString(),
      description: pkg.description || '',
      is_active: pkg.status
    });
    setEditingPackage(pkg);
    setShowAddModal(true);
  };

  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen dark:bg-gray-900 bg-gray-50">
        <AdminHeader />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading packages...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Package Management</h1>
            <p className="text-gray-600 mt-1">Create and manage savings packages</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-red-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-red-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>Add Package</span>
          </button>
        </div>

        {/* Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((pkg) => {
            const stats = packageStats[pkg._id] || { users_count: 0, total_deposits: 0 };

            return (
              <div key={pkg._id} className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{pkg.name}</h3>
                      <p className="text-gray-600 text-sm mt-1">{pkg.description}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleToggleStatus(pkg)}
                        className={`p-1 rounded transition-colors ${pkg.status ? 'text-green-600 hover:text-green-700' : 'text-red-600 hover:text-red-700'
                          }`}
                      >
                        {pkg.status ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => startEdit(pkg)}
                        className="p-1 text-blue-600 hover:text-blue-700 transition-colors"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(pkg._id)}
                        className="p-1 text-red-600 hover:text-red-700 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Package Details */}
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Minimum Deposit:</span>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(pkg.minDeposit)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Monthly Return:</span>
                      <span className="font-semibold text-green-600">
                        {pkg.monthlyReturn}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Referral Bonus:</span>
                      <span className="font-semibold text-purple-600">
                        {pkg.referralBonus}%
                      </span>
                    </div>
                  </div>

                  {/* Package Stats */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-1">
                          <Users className="h-4 w-4 text-blue-600 mr-1" />
                          <span className="text-lg font-bold text-gray-900">
                            {stats.users_count}
                          </span>
                        </div>
                        <span className="text-sm text-gray-600">Users</span>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-1">
                          <DollarSign className="h-4 w-4 text-green-600 mr-1" />
                          <span className="text-lg font-bold text-gray-900">
                            {formatCurrency(stats.total_deposits).replace('$', '')}
                          </span>
                        </div>
                        <span className="text-sm text-gray-600">Deposits</span>
                      </div>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="mt-4">
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${pkg.status
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                      }`}>
                      {pkg.status ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Add/Edit Package Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-gray-900">
                    {editingPackage ? 'Edit Package' : 'Add New Package'}
                  </h3>
                  <button
                    onClick={resetForm}
                    className="text-gray-400 hover:text-gray-600 p-1"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Display Name
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.display_name}
                      onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="e.g., Saver Plus"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Minimum Deposit ($)
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.01"
                      value={formData.minimum_deposit}
                      onChange={(e) => setFormData({ ...formData, minimum_deposit: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Package description..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Monthly Return Rate (%)
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      max="100"
                      step="0.01"
                      value={formData.monthly_return_rate}
                      onChange={(e) => setFormData({ ...formData, monthly_return_rate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Referral Bonus Rate (%)
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      max="100"
                      step="0.01"
                      value={formData.referral_bonus_rate}
                      onChange={(e) => setFormData({ ...formData, referral_bonus_rate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                    Package is active and visible to users
                  </label>
                </div>

                <div className="flex space-x-4 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <Save className="h-4 w-4" />
                    <span>{
                      working ? "processing..." :
                        editingPackage ? 'Update Package' : 'Create Package'
                    }</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
